import { ReactNode } from 'react';
import { Checkbox, CheckboxVariant } from '@/components';
import { cn } from '@/lib/utils';

interface AlertActionCardProps {
    icon: React.ComponentType<{ size?: number; className?: string }>;
    title: string;
    subtitle: string;
    enabled: boolean;
    isSelected: boolean;
    onClick: () => void;
    onRemove: () => void;
    chipLabel?: string;
    titleBadge?: ReactNode;
}

export function AlertActionCard({
    icon: Icon,
    title,
    subtitle,
    enabled,
    isSelected,
    onClick,
    onRemove,
    chipLabel,
    titleBadge,
}: AlertActionCardProps) {
    const showChip = !!chipLabel;

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (enabled) {
            if (isSelected) {
                onRemove();
            } else {
                onClick();
            }
        }
    };

    return (
        <div
            onClick={!enabled ? undefined : onClick}
            className={cn(
                'bg-grey-650 rounded-[8px] px-4 pt-3 pb-4 transition-all flex flex-col',
                !enabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
            )}
        >
            <div className="flex justify-between items-start mb-auto">
                <div className="flex-1" />
                <div onClick={handleCheckboxClick}>
                    <Checkbox
                        checked={isSelected}
                        disabled={!enabled}
                        variant={CheckboxVariant.WHITE}
                    />
                </div>
            </div>

            <div className="flex flex-col items-start justify-center flex-1">
                <div className="flex items-center gap-2 mb-4">
                    <Icon
                        size={24}
                        className={!enabled ? 'text-grey-300' : 'text-grey-200'}
                    />
                    {showChip && (
                        <div className="bg-grey-600 rounded-[4px] px-2.5 py-1 flex items-center">
                            <span className="text-xs-medium text-grey-200 leading-none">
                                {chipLabel}
                            </span>
                        </div>
                    )}
                </div>

                <div className="text-left">
                    <div className="flex items-center gap-2 mb-1">
                        <h3
                            className={cn(
                                'text-sm-medium',
                                !enabled ? 'text-grey-300' : 'text-white-100',
                            )}
                        >
                            {title}
                        </h3>
                        {titleBadge}
                    </div>
                    <p className="text-xs text-grey-300">{subtitle}</p>
                </div>
            </div>
        </div>
    );
}
